import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { Heading } from "../../ui/heading";
import { Button } from "../../ui/button";
import { Separator } from "../../ui/separator";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../ui/form";
import Sidebar from "../../DashboardNavigation/sidebar";
import Header from "../../DashboardNavigation/header";
import BreadCrumb from "../../breadcrumb";
import FileUpload from "../../FileUpload/FileUpload";
import {
  GallerieFormValues,
  gallerieSchema,
  UpdateGallerieFormValues,
  updateGallerieSchema,
} from "./gallerie-zod-schema";
import {
  getGallerieById,
  handleCreateGallerie,
  handleUpdateGallerie,
} from "../../../API/Mediatheque/Gallerie/gallerieService";
import { Input } from "../../ui/input";

export const GallerieForm: React.ComponentType<any> = () => {
  const uploadUrl = process.env.REACT_APP_FILE_UPLOAD;

  const router = useNavigate();
  const { id } = useParams();
  const initialData = id ? parseInt(id) : undefined;

  const [loading, setLoading] = useState(false);
  const title = initialData
    ? "Modifier la photo"
    : "Ajouter une nouvelle photo";
  const description = initialData
    ? "Modifier l'image"
    : "Ajouter une nouvelle photo";
  const action = initialData ? "Mise à jour" : "Ajouter";

  const form = useForm<GallerieFormValues | UpdateGallerieFormValues>({
    resolver: zodResolver(initialData ? updateGallerieSchema : gallerieSchema),
    defaultValues: {
      title: "",
      image: [],
    },
  });
  

  const onSubmit = async () => {
    setLoading(true);
    const formData = form.getValues();
    try {
      if (initialData) {
        await handleUpdateGallerie(initialData, formData, router);
      } else {
        await handleCreateGallerie(formData, router);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const [data, setData] = useState<any>();
  useEffect(() => {
    const fetchData = async () => {
      if (initialData) {
        const gallerieData = await getGallerieById(initialData);
        setData(gallerieData);
        form.reset({
          title: gallerieData.title,

          image: gallerieData.image
            ? [
                {
                  fileName: gallerieData.image,
                  name: gallerieData.image,
                  fileSize: 0,
                  size: 0,
                  key: gallerieData.image,
                },
              ]
            : [],
        });
      }
    };

    if (initialData) {
      fetchData();
    }
  }, [initialData]);

  const breadcrumbItems = [
    { title: "Photos", link: "/admin-dashboard/mediatheque/photos/" },
    { title: "Ajouter", link: "/admin-dashboard/mediatheque/photos/ajouter" },
  ];

  return (
    <>
      <Header />
      <div className="flex h-screen overflow-hidden">
        <Sidebar />
        <div className="w-full p-10">
          <BreadCrumb items={breadcrumbItems} />
          <div className="flex items-center justify-between">
            <Heading title={title} description={description} />
          </div>
          <Separator />
          <Form {...form}>
            <form
              className="w-full space-y-8"
              onSubmit={form.handleSubmit(onSubmit)}
            >
              <div className="flex justify-between">
                <div className="w-1/2">
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Titre </FormLabel>
                        <FormControl>
                          <Input
                            disabled={loading}
                            placeholder={"Titre..."}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
              {data?.image && (
                <div className="mt-4 mb-4">
                  <img
                    src={`${uploadUrl}/medias/${data.image}`}
                    alt="Old Image Preview"
                    className="w-10 h-auto"
                  />
                </div>
              )}
              <FormField
                control={form.control}
                name="image"
                render={() => (
                  <FormItem>
                    <FormControl>
                      <FileUpload
                        onChange={(files) => {
                          const mappedFiles = files.map((file) => ({
                            key: file.id,
                            fileName: file.fileName,
                            name: file.fileName,
                            fileSize: parseInt(file.fileSize),
                            size: parseInt(file.fileSize),
                            file: file.file,
                          }));
                          form.setValue("image", mappedFiles);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex space-x-5">
                <div>
                  <Button
                    disabled={loading}
                    isLoading={loading}
                    className="ml-auto"
                    type="submit"
                  >
                    {action}
                  </Button>
                </div>
                <div>
                  <Button
                    onClick={() => router(-1)}
                    disabled={loading}
                    isLoading={loading}
                    className="ml-auto bg-red-500"
                    type="button"
                  >
                    {"Annuler"}
                  </Button>
                </div>
              </div>
            </form>
          </Form>
        </div>
      </div>
    </>
  );
};
